/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by jessenelson on 9/15/15.
 */

function PackageMediaComponentVideoAssetSelectorController (
    this: JS5Object,
    _: JS5Object,
    AssetValidationService: JS5Object,
    DynamicPropertiesService: JS5Object,
    PackageFileUploadService: JS5Object,
    PackageMediaService: JS5Object,
    PackageUtilService: JS5Object,
    TempFileUploadService: JS5Object,
) {

    const setComponentVideo = (assetPath: string, isRemoteUploadEnabled: boolean) => {
        this.componentVideoAsset.acceptTemplateMember = this.getPrimaryAcceptTemplateMember();
        this.componentVideoAsset.virtualPath = assetPath;
        this.componentVideoAsset.assetId = -1;
        if (isRemoteUploadEnabled) {
            this.componentVideoAsset.pendingIngest = true;
        } else {
            delete this.componentVideoAsset.pendingIngest;
        }
        PackageUtilService.editorForm.$setDirty();
    };


    //================//
    // Template Logic //
    //================//

    this.remoteUploadEnabled = () => {
        return DynamicPropertiesService.getPropValueAsBoolean('mediaTabRemoteUpload.enabled', false);
    }

    const canUpload = () => {
        const hasMainVideoMediaInfo: boolean = !!PackageMediaService.getMainVideoMediaInfoXML();
        const ingestHasBegun = PackageMediaService.mainVideoIngestHasStarted();

        return hasMainVideoMediaInfo || ingestHasBegun;
    };

    const canSelectRemote = () => {
        let mainVideoPath = _.get(PackageMediaService.getMainVideo(), 'virtualPath');
        let mainVideoIsUpload = PackageMediaService.isAnUploadPath(mainVideoPath);
        let mainVideoIngestHasCompleted = PackageMediaService.mainVideoIngestHasCompleted();

        return !mainVideoIsUpload || mainVideoIngestHasCompleted;
    };

    this.getDisabledFileSources = () => {
        let newDisabledSources = [];
        if (!canUpload()) {
            newDisabledSources.push('ASPERA');
            newDisabledSources.push('LOCAL');
        }
        if (!canSelectRemote()) {
            newDisabledSources.push('REMOTE');
        }
        if (!_.isEqual(newDisabledSources, this.disabledFileSources)) {
            this.disabledFileSources = newDisabledSources;
        }
        return this.disabledFileSources;
    };

    this.getButtonTooltips = () => {
        let newTooltips: JS5Object = {};
        if (!canUpload()) {
            newTooltips.LOCAL =
                'Local-file ingest of this asset type will be' +
                ' enabled after the main video workflow has' +
                ' started.';
        }
        if (!canSelectRemote()) {
            newTooltips.REMOTE =
                    'Network-based ingest of this asset type will be' +
                    ' enabled after the main video workflow has' +
                    ' completed.'
        }
        if (!_.isEqual(newTooltips, this.buttonTooltips)) {
            this.buttonTooltips = newTooltips;
        }
        return this.buttonTooltips;
    };

    this.getPrimaryAcceptTemplateMember = () => {
        return PackageMediaService.getPrimaryAcceptTemplateMember();
    };

    this.onViewAssetButtonClick = ($event: JS5Object) => {
        $event.stopImmediatePropagation();
        PackageMediaService.openViewAssetModal(this.componentVideoAsset);
    };

    this.onQualityControlButtonClick = ($event: JS5Object) => {
        $event.stopImmediatePropagation();
        PackageMediaService.openQualityControlModal(this.componentVideoAsset);
    };

    this.onCurrentFileChanged = () => {
        PackageFileUploadService.unregisterLocalConfig(this.uploaderConfig);
        setComponentVideo(null, false);
        if (_.get(this, 'currentFile.status') === 'PASSED') {
            switch (this.currentFile.type) {
                case 'ASPERA':
                    // TODO: Test in UI when Aspera is configured
                    setComponentVideo(this.currentFile.results[0][0].uploadedData.asperaPath, true);
                    this.componentVideoAsset.mediaInfoDTO = this.currentFile.results[0][0].uploadedData.mediaInfoDTO;
                    break;
                case 'LOCAL':
                    // Display fileName (Correct virtualPath is set when media is saved)
                    PackageFileUploadService.registerLocalConfigForUpload(this.uploaderConfig);
                    setComponentVideo(`s3:fakePath/${this.currentFile.files[0].file.name}`, false);
                    this.componentVideoAsset.mediaInfoDTO = this.currentFile.results[0][0].uploadedData.mediaInfoDTO;
                    break;
                case 'REMOTE':
                    setComponentVideo(this.currentFile.files[0].virtualPath, false);
                    break;
            }
        }
    };


    //========================//
    // Remote File Validation //
    //========================//

    this.validateRemoteFile = (fileSystemItem: JS5Object) => {
        let assetPath = fileSystemItem.parent + fileSystemItem.name;
        let excludedComponentVideoIdList = PackageMediaService.getExcludedComponentIdList(this.componentRole);
        let newComponentVirtualPaths = PackageMediaService.getNewComponentVirtualPathList(this.componentRole);

        //
        // "this" component's component video is still active. If
        // it's already ingested, its ID should be added to the
        // excluded list. If it's not already ingested but has been
        // previously selected since the last save, its virtual
        // path needs to be removed from the "new" list.
        //

        if (_.has(this.componentVideoAsset, 'id')) {
            excludedComponentVideoIdList.push(this.componentVideoAsset.id);
        }
        _.pull(newComponentVirtualPaths, _.get(this, 'componentVideoAsset.virtualPath'));

        const acceptProfileId = PackageMediaService.getPrimaryAcceptTemplateMember().acceptProfile.id;
        newComponentVirtualPaths.push(assetPath);

        return AssetValidationService.validateComponentsRemoteMainVideoRemote(
            PackageMediaService.packageObject.id,
            newComponentVirtualPaths,
            acceptProfileId,
            this.componentRole.id,
            excludedComponentVideoIdList
        );
    };


    //========================//
    // Aspera File Validation //
    //========================//

    const validateLocalMedia = (localFile: File) => {
        let excludedComponentVideoIdList = PackageMediaService.getExcludedComponentIdList(this.componentRole);
        let newComponentVideoMediaInfoXMLList = PackageMediaService.getComponentVideoMediaInfoXMLList();

        let mainVideoMediaInfoXML = PackageMediaService.getMainVideoMediaInfoXML();
        let mainVideoMediaInfoDTO = null;
        if (mainVideoMediaInfoXML) {
            mainVideoMediaInfoDTO = {
                filePath: PackageMediaService.getMainVideo().virtualPath,
                mediaInfo: mainVideoMediaInfoXML
            };
        }

        return AssetValidationService.validateComponentsLocal(
            PackageMediaService.packageObject.id,
            newComponentVideoMediaInfoXMLList,
            localFile,
            PackageMediaService.getPrimaryAcceptTemplateMember().acceptProfile,
            this.componentRole.id,
            excludedComponentVideoIdList,
            mainVideoMediaInfoDTO
        );
    };

    this.validateAsperaFile = (asperaFileObject: JS5Object) => {
        return validateLocalMedia(asperaFileObject.file)
            .then((result: JS5Object) => {
                return PackageFileUploadService.parseAsperaValidationResult(PackageMediaService.packageObject, asperaFileObject, result[0]);
            });
    };


    //=======================//
    // Local File Validation //
    //=======================//

    this.getLocalFilePreValidationErrors = (file: File, filter: JS5Object) => {
        // Doesn't add any local file filters
    };

    this.validateLocalFile = (fileItem: JS5Object, uploaderConfig: JS5Object) => {
        fileItem.onVirtualPathSet = (virtualPath: string) => {
            setComponentVideo(virtualPath, true);
        };
        return validateLocalMedia(fileItem._file);
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.initializeLocalUploaderConfig = (uploaderConfig: JS5Object) => {
        TempFileUploadService.initialize(uploaderConfig);
        this.uploaderConfig = uploaderConfig;
    };

    this.$onChanges = (changes: JS5Object) => {
        if (changes.componentVideoAsset && this.uploaderConfig) {
            this.currentFile = null;
        }
    };

    this.$onDestroy = () => {
        PackageFileUploadService.unregisterLocalConfig(this.uploaderConfig);
    };
}

angular.module('prism-ui.package').component('packageMediaComponentVideoAssetSelector',{
    controller: PackageMediaComponentVideoAssetSelectorController,
    templateUrl: 'package/editor/components/view/media/assetSelector/videoComponent/package-media-component-video-asset-selector-template.html',
    bindings: {
        componentRole: '<',
        componentVideoAsset: '<',
        componentVideoMediaInfoXMLList: '<',
        listIndex: '<'
    }
});
